import { service } from '@ember/service';
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import type RouterService from '@ember/routing/router';
import type DemoModeService from '../services/demo-mode';
import type SessionContextService from '../services/session-context';
import type SessionService from '../services/session';
import pageTitle from 'ember-page-title/helpers/page-title';
import { t } from 'ember-intl';
import { NotificationsContainer } from 'tio-ui/components/notifications';
import DemoDataShortcut from 'tio-employee/components/demo-data-shortcut';
import companyTheme from 'tio-common/modifiers/company-theme';
import type PartnerService from '../services/partner';

// The boot shell in index.html sits outside Ember's tree, so it is found by id rather
// than through an element the app owns.
const dismissBootShell = modifier(() => {
  document.getElementById('tio-boot-shell')?.remove();
});

// Boot already delivers the theme (the application route's preregister load, or the
// authenticated /me companies), so this only revalidates when the tab comes back.
const refreshCompanyTheme = modifier(
  (element: HTMLElement, [refresh]: [(force?: boolean) => Promise<void>]) => {
    const document = element.ownerDocument;
    const window = document.defaultView;
    const onForeground = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window?.addEventListener('focus', onForeground);
    document.addEventListener('visibilitychange', onForeground);
    return () => {
      window?.removeEventListener('focus', onForeground);
      document.removeEventListener('visibilitychange', onForeground);
    };
  }
);

export default class ApplicationRouteComponent extends Component {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare demoMode: DemoModeService;
  @service declare partner: PartnerService;

  get isFullyAuthenticatedPage() {
    return this.sessionContext.isFullyAuthenticated;
  }

  <template>
    {{pageTitle (t "application_page_title")}}

    {{outlet}}

    <NotificationsContainer />
    {{#if this.demoMode.isAvailable}}
      <DemoDataShortcut />
    {{/if}}

    {{! Global portal target for Drawers and overlays to avoid parent portal nesting issues }}
    <div
      id="tio-drawer-target"
      {{dismissBootShell}}
      {{companyTheme this.partner.publicTheme documentRoot=true}}
      {{refreshCompanyTheme this.partner.refreshTheme}}
    ></div>
  </template>
}
